import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  useNotify
} from '@globaleris/ui.library'
import { Button, Card, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumSubTipoElemLog, EnumTipoSujeto } from '../../../api'
import { LogisticElementFilterModel } from '../../../api/Argentia/endpoints/logisticElements/model/logisticElements'
import {
  CUPSModel,
  DistributorModel,
  LogisticElementModel,
  UseModel
} from '../../../api/Argentia/model/contracts'
import Page from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { mapDirtyFormInputs } from '../../../helpers/dirtyFormInputsHelpers'
import { NonAuthRoutes } from '../../../model'
import { getActiveBalanceZone, getCustomerDomain } from '../../../redux'
import { selectClientData } from '../../../redux/selectors/client.selectors'
import { fetchSubjectsByTypeAndCommodity } from '../../clients/client.service'
import { MetersCard } from '../../meters/components/metersCard/metersCard'
import { MetersTable } from '../../meters/components/metersTable/metersTable'
import { UsesTable } from '../../uses/components/usesTable/usesTable'
import { mapUseRequestPatch } from '../../uses/uses.mapper'
import { UseModelPatch } from '../../uses/uses.model'
import { CupUsesCard } from '../components/cupUsesCard/cupUsesCard'
import { mapCupsDetailsEditFormData, mapModelToSelectOptionArray } from '../cups.mapper'
import {
  createCupsRef,
  createUseFromCups,
  deleteUseFromCups,
  fetchCUPSwithMetersAndUses,
  fetchLogisticElements,
  updateUseFromCups
} from '../cups.service'
import { styles } from '../cups.styles'

interface CupsDetailsPageProps {
  location?: {
    state: {
      CUPSCode?: string
    }
  }
}

export interface CupsDetailsEditForm {
  id?: number
  CUPSCode?: string
  distributorId?: number
  logisticElementId?: number
  distributorGroupId?: number
}

const defaultState: CupsDetailsEditForm = {}

export const CupsDetailsPage: React.FC<CupsDetailsPageProps> = props => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isLoadingCupUsesCard, setIsLoadingCupUsesCard] = useState<boolean>(false)
  const [isLoadingMetersCard, setIsLoadingMetersCard] = useState<boolean>(false)

  const [distributorIsLoading, setDistributorIsLoading] = useState<boolean>(false)
  const [logisticElementsIsLoading, setLogisticElementsIsLoading] = useState<boolean>(false)

  const [distributorsList, setDistributorsList] = useState<DistributorModel[]>([])
  const [logisticElementsList, setLogisticElementsList] = useState<LogisticElementModel[]>([])
  const [initialFormData, setInitialFormData] = useState<CupsDetailsEditForm>(defaultState)
  const [formData, setFormData] = useState<CupsDetailsEditForm>(defaultState)
  const [cupsData, setCupsData] = useState<CUPSModel[]>()
  const [cupsUsesData, setCupsUsesData] = useState<UseModel[]>([])
  const [cupsHasMetersWithUses, setCupsHasMetersWithUses] = useState<boolean>(false)

  const [t] = useTranslation(['globalerisLibrary', 'cups', 'enums', 'uses', 'simpleDialog'])
  const { info, error } = useNotify()

  const history = useHistory()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const clientData = useSelector(selectClientData)

  const CUPSCode = props?.location?.state?.CUPSCode
  const balanceZone = getActiveBalanceZone()
  const commodity = cupsData && cupsData[0].commodity

  // #region Handlers Starts
  const handleFormSave = async () => {
    const dirtyForm: CupsDetailsEditForm = mapDirtyFormInputs(initialFormData, formData)
    let promises: Promise<any>[] = [] // array to hold promises until Promise.allSettled is made

    if (dirtyForm.distributorId) {
      promises.push(
        createCupsRef(formData.id!, 'distribuidora', formData.distributorId!).then(response => {
          if (response.success) {
            info(t('cups:components.cupsDetails.forms.edit.save.distributorSuccess'))
            return response.success
          } else {
            error(t('cups:components.cupsDetails.forms.edit.save.distributorError'))
          }
        })
      )
    }

    if (dirtyForm.logisticElementId) {
      promises.push(
        createCupsRef(formData.id!, 'elementoLogistico', formData.logisticElementId!).then(
          response => {
            if (response.success) {
              info(t('cups:components.cupsDetails.forms.edit.save.logisticElementSuccess'))
              return response.success
            } else {
              error(t('cups:components.cupsDetails.forms.edit.save.logisticElementError'))
            }
          }
        )
      )
    }

    const results = await Promise.allSettled(promises)

    const allSuccessful = results.every(
      result => result.status === 'fulfilled' && result.value === true
    )

    if (allSuccessful) {
      history.push(NonAuthRoutes.ClientFile, {
        clientID: clientData?.id
      })
    }
  }

  const handleAddUse = async (row: UseModel) => {
    await createUseFromCups(row, cupsData?.[0]?.id!).then(async response => {
      if (response.success) {
        info(t('cups:components.cupUsesCard.notifications.addUse.success'))
        fetchCupsData()
      } else {
        error(
          response.error?.message ?? t('cups:components.cupUsesCard.notifications.addUse.error')
        )
      }
    })
  }

  const handleEditUse = async (rowData: UseModel, oldData: UseModel) => {
    const patchRequest: UseModelPatch = mapUseRequestPatch(rowData)
    await updateUseFromCups(patchRequest, cupsData?.[0]?.id!).then(async response => {
      if (response.success) {
        info(t('cups:components.cupUsesCard.notifications.editUse.success'))
        fetchCupsData()
      } else {
        error(
          response.error?.message ?? t('cups:components.cupUsesCard.notifications.editUse.error')
        )
      }
    })
  }

  const handleDeleteUse = async (row: UseModel) => {
    await deleteUseFromCups(row.id!, cupsData?.[0]?.id!).then(async response => {
      if (response.success) {
        info(t('cups:components.cupUsesCard.notifications.deleteUse.success'))
        fetchCupsData()
      } else {
        error(
          response.error?.message ?? t('cups:components.cupUsesCard.notifications.deleteUse.error')
        )
      }
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const metersHasUses = (cupsData: CUPSModel | undefined): boolean => {
    if (cupsData?.meters && cupsData.meters.length > 0) {
      return cupsData.meters.some(meter => {
        return meter.uses.length > 0 ? true : false
      })
    }
    return false
  }
  // #endregion Functions Ends

  // #region Form Handlers Starts
  const handleDistributorChange = (value: any, formState: CupsDetailsEditForm) => {
    const selectedDistributor = distributorsList.find(dist => dist.id === +value)
    const updatedFormData = {
      ...formState,
      distributorId: +value,
      distributorGroupId: selectedDistributor?.distributorGroup?.id
    }
    setFormData(updatedFormData)
  }
  // #endregion Form Handlers Ends

  // #region Form Definition Starts

  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('cups:components.cupsDetails.forms.edit.analytics')
    }
  }

  const selectedDistributor = formData.distributorId
    ? distributorsList.find(dist => dist.id === formData.distributorId)
    : undefined

  const formSection: Section[] = [
    {
      // title: t('documents:forms.add.title'),
      inputs: [
        {
          name: 'id',
          display: false
        },
        {
          name: 'CUPSCode',
          label: t('cups:components.cupsDetails.forms.edit.inputs.CUPSCode'),
          type: DataType.TEXT,
          readOnly: true
        },
        {
          name: 'distributorId',
          label: t('cups:components.cupsDetails.forms.edit.inputs.distributor'),
          type: DataType.SELECT,
          isLoading: distributorIsLoading,
          selectOptions: mapModelToSelectOptionArray(distributorsList),
          onChange: handleDistributorChange
        },
        {
          name: 'distributorGroupId',
          label: t('cups:components.cupsDetails.forms.edit.inputs.distributorGroup'),
          type: DataType.SELECT,
          readOnly: true,
          isLoading: distributorIsLoading,
          selectOptions: mapModelToSelectOptionArray(distributorsList),
          display: selectedDistributor?.distributorGroup?.id != null
        },
        {
          name: 'logisticElementId',
          label: t('cups:components.cupsDetails.forms.edit.inputs.connectionPoint'),
          type: DataType.SELECT,
          display: commodity === EnumCommodity.GasNatural,
          isLoading: logisticElementsIsLoading,
          selectOptions: mapModelToSelectOptionArray(logisticElementsList, true)
        }
      ]
    }
  ]

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const fetchCupsData = useCallback(async () => {
    if (CUPSCode != undefined) {
      setIsFetching(true)
      setIsLoadingCupUsesCard(true)
      setIsLoadingMetersCard(true)
      await fetchCUPSwithMetersAndUses(CUPSCode).then(response => {
        if (response.success) {
          setCupsData(response.data)
          setCupsUsesData(response.data?.[0]?.uses ?? [])
          //setCupsHasMetersWithUses(metersHasUses(response.data?.[0])) US73285
          setInitialFormData(mapCupsDetailsEditFormData(response.data))
          setFormData(mapCupsDetailsEditFormData(response.data))
        } else {
          error(t('cups:notifications.fetchCupsError'))
        }
        setIsFetching(false)
        setIsLoadingCupUsesCard(false)
        setIsLoadingMetersCard(false)
      })
    }
  }, [CUPSCode])

  useEffect(() => {
    // CupsData Fetch
    fetchCupsData()

    // LogisticElement Fetch
    setLogisticElementsIsLoading(true)
    const logisticElementFilter: LogisticElementFilterModel = {
      subType: [
        EnumSubTipoElemLog.PTO_PCTD,
        EnumSubTipoElemLog.PTO_PCDD,
        EnumSubTipoElemLog.PTO_PCLD
      ]
    }
    fetchLogisticElements(logisticElementFilter).then(response => {
      if (response.success && response.data) {
        setLogisticElementsList(response.data.filter(el => el.code && el.code.trim() !== ''))
      } else {
        error(t('cups:notifications.logisticElementError'))
      }
      setLogisticElementsIsLoading(false)
    })
  }, [fetchCupsData])

  useEffect(() => {
    // Distributor Fetch
    if (commodity) {
      setDistributorIsLoading(true)
      fetchSubjectsByTypeAndCommodity(EnumTipoSujeto.Distribuidor, commodity).then(response => {
        if (response.success) {
          setDistributorsList(response.data)
        } else {
          error(t('cups:notifications.distributorError'))
        }
        setDistributorIsLoading(false)
      })
    }
  }, [commodity])

  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('cups:components.cupsDetails.title')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={() => {
            history.push(NonAuthRoutes.ClientFile, { clientID: clientData?.id })
          }}
        >
          {t('cups:components.cupsDetails.actions.back')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isFetching || distributorIsLoading || logisticElementsIsLoading}
          onClick={handleFormSave}
        >
          {t('cups:components.cupsDetails.actions.save')}
        </Button>
      </PageHeader>

      <PageCenterColumn>
        {isFetching ? (
          <FormSkeleton withTitle={true} columns={3} inputs={3} elevation={1} />
        ) : (
          <Card raised className={classes.card}>
            <Form
              title={t('cups:components.cupsDetails.forms.edit.title')}
              sections={formSection}
              data={formData}
              options={options}
            />
          </Card>
        )}

        <CupUsesCard cupsHasMetersWithUses={cupsHasMetersWithUses} isLoading={isLoadingCupUsesCard}>
          <UsesTable
            listUsesData={cupsUsesData}
            handleAddUse={handleAddUse}
            handleEditUse={handleEditUse}
            handleDeleteUse={handleDeleteUse}
            fabAndRowActionsAllowed={!cupsHasMetersWithUses}
            commodity={commodity}
          />
        </CupUsesCard>

        <MetersCard isLoading={isLoadingMetersCard}>
          <MetersTable cupsData={cupsData} onMeterUpdate={fetchCupsData} />
        </MetersCard>
      </PageCenterColumn>
    </Page>
  )
}
